import {useEffect, useState} from "react";
import {ReactIsland, type WithCommandListener} from "@/shared/reactIsland";
import {CutHandlingConfigurationPanel} from "@/features/admin/breakModel/CutHandlingConfigurationPanel";
import type {CutHandlingConfiguration} from "@/features/admin/breakModel/cutHandlingConfigurationModel";
import type {FieldError} from "@/shared/timr";

export type {CutHandlingConfiguration};

interface CutHandlingConfigurationPanelState {
	configuration: CutHandlingConfiguration;
	/** Server-side validation errors that belong to cut-handling fields. */
	fieldErrors: FieldError[];
}

export type CutHandlingConfigurationPanelIslandCommands = {
	/** Re-renders the panel from the legacy dialog state; nothing is rendered before the first update. */
	update: CutHandlingConfigurationPanelState;
	/** Re-seeds the panel's own threshold state, e.g. when another break model is loaded. */
	reset: void;
};

export interface CutHandlingConfigurationPanelIslandProps {
	onChange: (configuration: CutHandlingConfiguration) => void;
}

export function CutHandlingConfigurationPanelIsland({
	addCommandListener,
	onChange,
}: CutHandlingConfigurationPanelIslandProps & WithCommandListener<CutHandlingConfigurationPanelIslandCommands>) {
	const [state, setState] = useState<CutHandlingConfigurationPanelState | null>(null);
	const [resetCount, setResetCount] = useState(0);

	useEffect(() => addCommandListener("update", setState), [addCommandListener]);
	useEffect(
		() => addCommandListener("reset", () => setResetCount(count => count + 1)),
		[addCommandListener],
	);

	if (state == null) {
		return <></>;
	}

	return (
		<CutHandlingConfigurationPanel
			key={resetCount}
			configuration={state.configuration}
			fieldErrors={state.fieldErrors}
			onChange={onChange}
		/>
	);
}

export function mountCutHandlingConfigurationPanelIsland(
	mountId: string,
	onChange: (configuration: CutHandlingConfiguration) => void,
): ReactIsland<CutHandlingConfigurationPanelIslandProps, CutHandlingConfigurationPanelIslandCommands> | undefined {
	const mount = document.getElementById(mountId);
	if (!mount) {
		return undefined;
	}

	return new ReactIsland<CutHandlingConfigurationPanelIslandProps, CutHandlingConfigurationPanelIslandCommands>(
		CutHandlingConfigurationPanelIsland,
		{onChange},
		mount,
	);
}
